import $ from 'jquery';
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import {liteClient} from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import { configure, refinementList } from 'instantsearch.js/es/widgets';
import { connectSearchBox } from 'instantsearch.js/es/connectors';
import { connectHits, connectPagination } from 'instantsearch.js/es/connectors';
import { history } from 'instantsearch.js/es/lib/routers';
import { jobHit } from '../components/jobHit';
import searchBox from '../components/searchBox';
import { algoliaAppId, algoliaApiKey, citiesSearchIndex, jobsSearchIndex } from '../variables';
import { lockBody, unlockBody } from '../utils';
import { dynamicUriTemplate, extractParamsFromUri, extractPageFromUri } from '../functions';
import showMoreText from '../components/showMoreText';

// eslint-disable-next-line no-undef
const { __ } = wp.i18n;

// eslint-disable-next-line no-undef
const object = vnObject;

const {
    mainSlug,
    query,
    agency,
    jobTypeContract,
    jobBusinessSector,
    city,
    latlng,
    radius,
} = object.jobsQueryStrings;

const slugs = {
    query,
    agency,
    jobTypeContract,
    jobBusinessSector,
    city,
    latlng,
    radius,
};

var currentCity = '';
var currentLatlng = '';
var currentRadius = '';
// The page of the URL (/page/N/) is kept until the user changes the search
var keepPage = true;

const hitsPerPage = 30;

let jobsSearch;
let citySearch;
const checkEl = document.querySelector('.pt-jobs__posts');
if (checkEl) {
    const searchClient = liteClient(algoliaAppId, algoliaApiKey);

    const buildURL = (origin, routeState) => {
        const params = {
            query: routeState.query,
            city: currentCity,
            latlng: currentLatlng,
            // The radius is useless without a position
            radius: currentLatlng ? currentRadius : '',
            agency: routeState.agency,
            jobTypeContract: routeState.jobTypeContract,
            jobBusinessSector: routeState.jobBusinessSector,
        };

        const url = dynamicUriTemplate(origin, params, mainSlug, slugs);

        return routeState.page > 1 ? `${url}page/${routeState.page}/` : url;
    };

    const stateMapping = {
        stateToRoute(uiState) {
            const indexUiState = uiState[jobsSearchIndex] || {};

            const res = {
                query: indexUiState.query,
                city: currentCity,
                latlng: currentLatlng,
                radius: currentRadius,
                jobBusinessSector: indexUiState.refinementList?.['jobBusinessSector.name'],
                jobTypeContract: indexUiState.refinementList?.['jobTypeContract.name'],
                agency: indexUiState.refinementList?.['agency.name'],
                page: indexUiState.page,
            };

            return res;
        },

        routeToState(routeState) {
            currentCity = routeState.city;
            currentLatlng = routeState.latlng;
            currentRadius = routeState.radius;
            if (routeState.radius == undefined) {
                currentRadius = 1000;
            }

            return {
                [jobsSearchIndex]: {
                    query: routeState.query,
                    city: routeState.city,
                    latlng: routeState.latlng,
                    radius: routeState.radius,
                    page: routeState.page,
                    refinementList: {
                        'jobBusinessSector.name': routeState.jobBusinessSector,
                        'jobTypeContract.name': routeState.jobTypeContract,
                        'agency.name': routeState.agency,
                    },
                },
            };
        },
    };

    // URL of a page of the current search, for the pagination links
    const pageURL = (page) => buildURL(
        window.location.origin,
        { ...stateMapping.stateToRoute(jobsSearch.getUiState()), page }
    );

    jobsSearch = instantsearch({
        indexName: jobsSearchIndex,
        indexId: jobsSearchIndex,
        searchClient,
        insights: true,
        clickAnalytics: true,
        routing: {
            router: history({
                createURL({ location, routeState }) {
                    var $checkbox = $('input[name="radius"][value="' + currentRadius + '"]');
                    if ($checkbox.length > 0) {
                        $checkbox.prop('checked', true);
                        setRadius();
                    }

                    return buildURL(location.origin, routeState);
                },

                parseURL({ location }) {
                    const params = extractParamsFromUri(location.href, slugs);

                    return {
                        query: params[slugs.query] && params[slugs.query][0],
                        city: params[slugs.city],
                        latlng: params[slugs.latlng],
                        radius: params[slugs.radius],
                        agency: params[slugs.agency],
                        jobTypeContract: params[slugs.jobTypeContract],
                        jobBusinessSector: params[slugs.jobBusinessSector],
                        page: extractPageFromUri(location.href),
                    };
                },
            }),

            stateMapping,
        },
    });

    const customSearchBox = connectSearchBox(searchBox);
    const customHits = connectHits((renderOptions, isFirstRender) => {
        const { hits, widgetParams } = renderOptions;

        // Keep the hits rendered server side (page-jobs.php) until the results arrive
        if (isFirstRender) {
            return;
        }

        widgetParams.container.querySelector('.grid').innerHTML = hits
            .map((hit) => jobHit(hit))
            .join('');
    });

    // Same markup as paginate_links() in vn_jobs_pagination()
    const paginationItems = (current, total) => {
        const items = [];
        let dots = false;

        for (let n = 1; n <= total; n++) {
            if (n === current) {
                items.push(`<span aria-current="page" class="page-numbers current">${n}</span>`);
                dots = true;
            } else if (n <= 1 || (n >= current - 1 && n <= current + 1) || n > total - 1) {
                items.push(`<a class="page-numbers" href="${pageURL(n)}" data-page="${n}">${n}</a>`);
                dots = true;
            } else if (dots) {
                items.push('<span class="page-numbers dots">&hellip;</span>');
                dots = false;
            }
        }

        return items.map((item) => `<li>${item}</li>`).join('');
    };

    const customPagination = connectPagination((renderOptions, isFirstRender) => {
        const { currentRefinement, nbPages, refine, widgetParams } = renderOptions;
        const container = widgetParams.container;

        if (isFirstRender) {
            container.addEventListener('click', (e) => {
                const link = e.target.closest('a[data-page]');
                if (!link) {
                    return;
                }

                e.preventDefault();
                refine(parseInt(link.dataset.page, 10) - 1);
                checkEl.scrollIntoView({ behavior: 'smooth' });
            });

            // Keep the pagination rendered server side until the results arrive
            return;
        }

        container.innerHTML = nbPages > 1
            ? `<div class="vn-pagination"><ul class="page-numbers">${paginationItems(currentRefinement + 1, nbPages)}</ul></div>`
            : '';
    });

    jobsSearch.addWidgets([
        customSearchBox({
            container: document.querySelector('.jobs-search-handler'),
            placeholder: __('Mason, Painter...', 'vn'),
            queryHook(query, search) {
                $('.js-search-result').html(query);
                search(query);
            },
        }),

        refinementList({
            container: '.types-contract-handler',
            attribute: 'jobTypeContract.name',
            sortBy: ['count:desc'],
            limit: 999,
            transformItems(items, { results }) {
                if (results._state.disjunctiveFacetsRefinements['agency.name']) {
                    $('.js-agency-result').html(results._state.disjunctiveFacetsRefinements['agency.name'].join(', '));
                }
                if (results._state.disjunctiveFacetsRefinements['jobTypeContract.name']) {
                    $('.js-contract-result').html(results._state.disjunctiveFacetsRefinements['jobTypeContract.name'].join(', '));
                }
                if (results._state.disjunctiveFacetsRefinements['jobBusinessSector.name']) {
                    $('.js-sector-result').html(results._state.disjunctiveFacetsRefinements['jobBusinessSector.name'].join(', '));
                }

                return items.map((item) => ({
                    ...item,
                    highlighted: item.highlighted.toUpperCase(),
                }));
            },
        }),

        refinementList({
            container: '.agency-handler',
            attribute: 'agency.name',
            sortBy: ['count:desc'],
            limit: 999,
            transformItems(items, { results }) {
                if (results._state.disjunctiveFacetsRefinements['agency.name']) {
                    $('.js-agency-result').html(results._state.disjunctiveFacetsRefinements['agency.name'].join(', '));
                }
                if (results._state.disjunctiveFacetsRefinements['jobTypeContract.name']) {
                    $('.js-contract-result').html(results._state.disjunctiveFacetsRefinements['jobTypeContract.name'].join(', '));
                }
                if (results._state.disjunctiveFacetsRefinements['jobBusinessSector.name']) {
                    $('.js-sector-result').html(results._state.disjunctiveFacetsRefinements['jobBusinessSector.name'].join(', '));
                }

                return items.map((item) => ({
                    ...item,
                }));
            },
        }),

        refinementList({
            container: '.business-sectors-handler',
            attribute: 'jobBusinessSector.name',
            sortBy: ['name'],
            limit: 5,
            showMore: true,
            showMoreLimit: 999,
            searchablePlaceholder: __('Search sector of activity', 'vn'),
            cssClasses: {
                showMore: ['ais-RefinementList-showMore', 'vn-btn vn-btn--blue'],
            },
            templates: {
                showMoreText(data) {
                    return showMoreText(data);
                },
            },
        }),

        customHits({
            container: document.querySelector('.jobs-hits-handler'),
        }),

        customPagination({
            container: document.querySelector('.jobs-pagination-handler'),
        }),

        configure({
            hitsPerPage,
        }),
    ]);

    jobsSearch.start();

    citySearch = autocomplete({
        container: '.cities-search-handler',
        detachedMediaQuery: 'none',
        placeholder: __('Montpellier, Aix-en-Provence...', 'vn'),
        onReset() {
            setQueryParameter('aroundLatLng', '');
        },
        onStateChange({ prevState, state }) {
            if (state.query != undefined && state.query.length == 0) {
                setQueryParameter('aroundLatLng', '');
                $('.js-where-result').html('');
                currentCity = '';
                currentLatlng = '';
            }
        },
        getSources() {
            return [{
                sourceId: 'cities',
                getItems({ query }) {
                    return getAlgoliaResults({
                        searchClient,
                        queries: [
                            {
                                indexName: citiesSearchIndex,
                                query,
                            },
                        ],
                    });
                },
                templates: {
                    item({ item }) {
                        var postalCode = item.codesPostaux.substring(0, 2);
                        return `${item.nom} ( ${postalCode} )`;
                    },
                },
                onSelect: function (event) {
                    let latLng = parseFloat(event.item.latitude) + ',' + parseFloat(event.item.longitude);
                    setQueryParameter('aroundLatLng', latLng);
                    var postalCode = event.item.codesPostaux.substring(0, 2);
                    event.setQuery(event.item.nom + ' ( ' + postalCode + ' )');
                    $('.js-where-result').html(event.item.nom + ' ( ' + postalCode + ' )');
                    currentCity = event.item.nom + ' ( ' + postalCode + ' )';

                    currentLatlng = latLng;
                },
            }];
        },
    });

    function setQueryParameter(parameter, value) {
        const { helper } = jobsSearch;

        if (helper.state[parameter] === value) {
            return;
        }

        if (keepPage) {
            helper.setState(helper.state.setQueryParameter(parameter, value)).search();
        } else {
            helper.setQueryParameter(parameter, value).search();
        }
    }

    $('input[name="radius"][value="' + currentRadius + '"]').prop('checked', true);
    setRadius();
    $('input[name="radius"]').change(function () {
        setRadius();
    });

    function setRadius() {
        var selectedRadius = $('input[name="radius"]:checked').val();
        if (selectedRadius) {
            var radiusInMeter = selectedRadius * 1000;
            setQueryParameter('aroundRadius', radiusInMeter);
            $('.js-radius-result').html(selectedRadius + ' km');
            currentRadius = selectedRadius;
        }
    }

    if (currentCity) {
        citySearch.setQuery(currentCity);
    }

    if (currentLatlng) {
        var coordinates = currentLatlng.toString().split(',');
        var latitude = parseFloat(coordinates[0]);
        var longitude = parseFloat(coordinates[1]);
        setQueryParameter('aroundLatLng', latitude + ',' + longitude);
    }

    keepPage = false;
}

const openFilter = () => {
    document.querySelector('.filter-sidebar-col').classList.add('active');
    lockBody();
}

const closeFilter = () => {
    document.querySelector('.filter-sidebar-col').classList.remove('active');
    unlockBody();
    document
        .querySelector('.pt-jobs__posts')
        .scrollIntoView({ behavior: 'smooth' });
}

const opener = document.querySelector('.filter-sidebar-opener');
const filterEl = document.querySelector('.filter-sidebar-col');
const closers = document.querySelectorAll('.filter-actions button');

opener && opener.addEventListener('click', openFilter);
filterEl &&
filterEl.addEventListener(
    'click',
    (e) => e.target === filterEl && closeFilter()
);
if (closers.length) {
    ;[...closers].map((closer) => {
        closer.addEventListener('click', closeFilter);
    });
}

$('.js-widget-title').on('click', function () {
    if (!window.matchMedia('(max-width: 991.98px)').matches) {
        return;
    }

    const title = $(this);
    const parent = title.parent();
    const content = parent.find('.js-widget-content');

    $('.js-widget-active').not(parent).removeClass('js-widget-active').find('.js-widget-content').slideToggle(250);

    content.slideToggle(250);
    parent.toggleClass('js-widget-active');
});

$('.js-back-filters').on('click', function () {
    document
        .querySelector('.filter-sidebar')
        .scrollIntoView({ behavior: 'smooth' });
});

$('.js-hide-filters').on('click', function () {
    $('.js-sidebar').addClass('closed');
    unlockBody();
});

$('.js-open-filters').on('click', function () {
    $('.js-sidebar').removeClass('closed');
    lockBody();
});

export { jobsSearch };
